import React, { useState, useEffect } from 'react';

// Interfaces for both components
interface ValueAddedProduct {
  id?: number;
  productType: string;
  description: string;
  value: string;
  premium: string;
  isEditing?: boolean;
}

interface QuoteValueAddedModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  clientId: number;
  setSaveStatus: (status: 'idle' | 'success' | 'error') => void;
  setSaveMessage: (message: string) => void;
  valueAddedProduct?: any;
  mode?: 'create' | 'edit' | 'view';
}

interface EditableValueAddedCardProps {
  valueAddedProduct: ValueAddedProduct;
  index: number;
  onSave: (index: number, updatedData: any) => void;
  onCancel: (index: number) => void;
  onEdit: (index: number) => void;
  cardNumber?: number;
  cardTitle?: string;
  showCardNumber?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL;

// Currency formatting helper functions
const formatCurrencyInput = (value: string | number): string => {
  if (!value && value !== 0) return '';
  
  // Convert to string if it's a number
  const stringValue = typeof value === 'number' ? value.toString() : value;
  
  // Remove any existing formatting except decimal point
  const numericValue = stringValue.replace(/[^\d.]/g, '');
  
  // Handle cases where there are multiple decimal points
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  
  // If there are multiple decimal points, take only the first two parts
  if (parts.length > 2) {
    wholePart = parts[0];
    decimalPart = parts[1];
  }
  
  // Limit decimal to 2 digits
  if (decimalPart.length > 2) {
    decimalPart = decimalPart.substring(0, 2);
  }
  
  // Format the whole number part with commas - ONLY if it's not empty
  if (wholePart) {
    // Remove any existing commas and parse as integer
    const cleanWholePart = wholePart.replace(/,/g, '');
    if (cleanWholePart) {
      wholePart = parseInt(cleanWholePart, 10).toLocaleString('en-US');
    }
  }
  
  // Combine parts
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
};

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  
  // Remove all commas and parse as float
  const cleanValue = value.replace(/,/g, '');
  
  // Handle cases where the value might have multiple decimal points
  const parts = cleanValue.split('.');
  if (parts.length > 2) {
    // If there are multiple decimal points, take the first part as whole number
    // and the second part as decimal
    const wholePart = parts[0] || '0';
    const decimalPart = parts[1] || '00';
    return parseFloat(`${wholePart}.${decimalPart}`);
  }
  
  return parseFloat(cleanValue) || 0;
};

// Main Modal Component
export const QuoteValueAddedModal: React.FC<QuoteValueAddedModalProps> = ({
  isVisible,
  onClose,
  onSave,
  clientId,
  setSaveStatus,
  setSaveMessage,
  valueAddedProduct,
  mode = 'create',
}) => {
  const [formData, setFormData] = useState({
    productType: '',
    description: '',
    value: '',
    premium: '',
  });

  const [currencyDisplay, setCurrencyDisplay] = useState({
    value: '',
    premium: '',
  });

  const [errors, setErrors] = useState({
    productType: '',
    description: '',
    value: '',
    premium: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debug modal state changes
  useEffect(() => {
    console.log('🔍 Modal visibility changed:', isVisible);
    if (isVisible) {
      console.log('✅ Modal opened, form reset');
      setIsSubmitting(false);
    }
  }, [isVisible]);

    // Pre-fill form when in edit mode or when product data changes
    useEffect(() => {
      if (isVisible && mode === 'edit' && valueAddedProduct) {
        console.log('🔍 Pre-filling form with product data:', valueAddedProduct);
        
        const productData = valueAddedProduct.data || valueAddedProduct;
        
        setFormData({
          productType: productData.productType || productData.product_type || '',
          description: productData.description || '',
          value: productData.value?.toString() || productData.insuredValue?.toString() || '',
          premium: productData.premium?.toString() || '',
        });
        
        setCurrencyDisplay({
          value: formatCurrencyInput(productData.value?.toString() || productData.insuredValue?.toString() || ''),
          premium: formatCurrencyInput(productData.premium?.toString() || ''),
        });
      } else if (isVisible && mode === 'create') {
        // Reset form for new product
        console.log('🆕 Resetting form for new product');
        setFormData({
          productType: '',
          description: '',
          value: '',
          premium: '',
        });
        setCurrencyDisplay({
          value: '',
          premium: '',
        });
      }
    }, [isVisible, mode, valueAddedProduct]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'value' || name === 'premium') {
      // Handle currency fields with formatting
      const formattedValue = formatCurrencyInput(value);
      const numericValue = parseCurrencyInput(value);
      
      console.log(`💰 Currency change for ${name}:`, {
        input: value,
        formatted: formattedValue,
        numeric: numericValue
      });
      
      setFormData(prev => ({
        ...prev,
        [name]: numericValue ? numericValue.toString() : '',
      }));
      
      setCurrencyDisplay(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // blur handlers for final formatting
  const handleCurrencyBlur = (field: 'value' | 'premium') => {
    const currentValue = currencyDisplay[field];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [field]: formatted }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {
      productType: !formData.productType ? 'Product Type is required' : '',
      description: !formData.description ? 'Description is required' : '',
      value: '',
      premium: '',
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Parse currency values
      const valueAsNumber = parseCurrencyInput(currencyDisplay.value);
      const premiumAsNumber = parseCurrencyInput(currencyDisplay.premium);

      console.log('🔄 Preparing value added product data (NO API CALL):', {
        mode,
        productType: formData.productType,
        description: formData.description,
        value: valueAsNumber,
        premium: premiumAsNumber,
        existingProductId: valueAddedProduct?.id,
        clientId: clientId,
      });

      // Extract quote ID if available
      const originalQuoteId = valueAddedProduct?.quoteId || 
                       valueAddedProduct?.data?.quote_id || 
                       valueAddedProduct?.data?.quoteId ||
                       valueAddedProduct?.quote_id ||
                       (valueAddedProduct?.data ? valueAddedProduct.data.quote_id : undefined);

      // Prepare the data WITHOUT making API calls
      const productForCard = {
        id: valueAddedProduct?.id, // Keep existing ID for editing
        productType: formData.productType,
        description: formData.description,
        value: valueAsNumber.toString(),
        premium: premiumAsNumber.toString(),
        isEditing: false,
        data: {
          ...(valueAddedProduct?.data || valueAddedProduct || {}),
          id: valueAddedProduct?.id, // Only use existing ID
          product_type: formData.productType,
          description: formData.description,
          value: valueAsNumber.toString(),
          premium: premiumAsNumber.toString(),
          client_id: clientId,
          // Preserve the quote_id if it exists
          quote_id: originalQuoteId,
        },
        // Preserve the quoteId for the master page
        quoteId: originalQuoteId,
        quoteReference: valueAddedProduct?.quoteReference,
        // Add a temporary ID for new items if needed
        ...(mode === 'create' && !valueAddedProduct?.id ? { tempId: Date.now() } : {})
      };
      
      console.log('📤 Calling onSave with product data (NO DB SAVE):', productForCard);
      
      // Call onSave with the formatted data
      onSave(productForCard);
      
      // Don't set save status/message here since we're not saving to DB
      // setSaveStatus('success');
      // setSaveMessage('Value Added product added successfully!');
      
      handleClose();
      
    } catch (error) {
      console.error('❌ Error preparing value-added product:', error);
      // Don't set save status/message since we're not saving to DB
      // setSaveStatus('error');
      // setSaveMessage('Failed to prepare value-added product');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
  console.log('🔒 Modal closing, resetting form');
  setFormData({
    productType: '',
    description: '',
    value: '',
    premium: '',
  });
  setCurrencyDisplay({
    value: '',
    premium: '',
  });
  setErrors({
    productType: '',
    description: '',
    value: '',
    premium: '',
  });
  setIsSubmitting(false); // Reset submission state
  onClose();
};

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">
          {mode === 'edit' ? 'Edit Value Added Product' : 'Add Value Added Product'}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mb-6">
            {/* Row 1: Product Type, Value */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="productType" className="block text-gray-700 font-bold mb-2 text-sm">
                  Product Type
                </label>
                <select
                  id="productType"
                  name="productType"
                  value={formData.productType}
                  onChange={handleChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                >
                  <option value="">Select product type</option>
                  <option value="Vehicle">Vehicle</option>
                  <option value="Building">Building</option>
                  <option value="Home Content">Home Content</option>
                  <option value="All Risk Item">All Risk Item</option>
                  <option value="Trailer">Trailer</option>
                  <option value="Caravan">Caravan</option>
                  <option value="General">General</option>
                  <option value="Other">Other</option>
                </select>
                {errors.productType && <p className="text-red-500 text-xs mt-1">{errors.productType}</p>}
              </div>

              <div>
                <label htmlFor="value" className="block text-gray-700 font-bold mb-2 text-sm">
                  Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">R</span>
                  </div>
                  <input
                    type="text"
                    id="value"
                    name="value"
                    value={currencyDisplay.value}
                    onChange={handleChange}
                    onBlur={() => handleCurrencyBlur('value')}
                    className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    placeholder="0.00"
                  />
                </div>
                {errors.value && <p className="text-red-500 text-xs mt-1">{errors.value}</p>}
              </div>
            </div>

            {/* Row 2: Description */}
            <div>
              <label htmlFor="description" className="block text-gray-700 font-bold mb-2 text-sm">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                rows={4}
                placeholder="Enter product description"
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            {/* Row 3: Premium */}
            <div>
              <label htmlFor="premium" className="block text-gray-700 font-bold mb-2 text-sm">
                Premium
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">R</span>
                </div>
                <input
                  type="text"
                  id="premium"
                  name="premium"
                  value={currencyDisplay.premium}
                  onChange={handleChange}
                  onBlur={() => handleCurrencyBlur('premium')}
                  className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : (mode === 'edit' ? 'Update' : 'Add Product')}
            </button>

          </div>
        </form>
      </div>
    </div>
  );
};

// Editable Card Component
export const EditableValueAddedCard: React.FC<EditableValueAddedCardProps> = ({
  valueAddedProduct,
  index,
  onSave,
  onCancel,
  onEdit,
  cardNumber,
  cardTitle = 'Value Added',
  showCardNumber = true
}) => {
  const [formData, setFormData] = useState({
    productType: valueAddedProduct.productType || '',
    description: valueAddedProduct.description || '',
    value: valueAddedProduct.value || '',
    premium: valueAddedProduct.premium || '',
  });

  const [currencyDisplay, setCurrencyDisplay] = useState({
    value: formatCurrencyInput(valueAddedProduct.value?.toString() || ''),
    premium: formatCurrencyInput(valueAddedProduct.premium?.toString() || ''),
  });

  const [errors, setErrors] = useState({
    productType: '',
    description: '',
    value: '',
    premium: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'value' || name === 'premium') {
      const formattedValue = formatCurrencyInput(value);
      const numericValue = parseCurrencyInput(value);
      
      setFormData(prev => ({
        ...prev,
        [name]: numericValue ? numericValue.toString() : '',
      }));
      
      setCurrencyDisplay(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCurrencyBlur = (field: 'value' | 'premium') => {
    const currentValue = currencyDisplay[field];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [field]: formatted }));
      }
    }
  };

  const validateForm = () => {
    // No validation for editing - all fields are optional
    const newErrors = {
      productType: '',
      description: '',
      value: '',
      premium: '',
    };
    
    setErrors(newErrors);
    return true; // Always return true for editing
  };

  const handleSave = () => {
    if (!validateForm()) return;
    
    const updatedData = {
      productType: formData.productType || valueAddedProduct.productType,
      description: formData.description || valueAddedProduct.description,
      value: formData.value ? parseFloat(formData.value) : (typeof valueAddedProduct.value === 'string' ? parseFloat(valueAddedProduct.value) : valueAddedProduct.value),
      premium: formData.premium ? parseFloat(formData.premium) : (typeof valueAddedProduct.premium === 'string' ? parseFloat(valueAddedProduct.premium) : valueAddedProduct.premium),
      id: valueAddedProduct.id,
      isEditing: false
    };
    
    onSave(index, updatedData);
  };

  const handleCancel = () => {
    onCancel(index);
  };

  // If not in editing mode, show the display card
  if (!valueAddedProduct.isEditing) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-teal-600">
              {showCardNumber ? `${cardTitle} #${cardNumber}` : cardTitle}
            </h4>
            {/* REMOVED: The bubble span element */}
            <button
              onClick={() => onEdit(index)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Edit
            </button>
          </div>

          <div className="space-y-2 text-sm">
            {/* Product Type */}
            <div className="flex justify-between">
              <span className="text-gray-600">Item:</span>
              <span className="font-medium text-right max-w-[150px]">
                {valueAddedProduct.productType || valueAddedProduct.productType || 'Not specified'}
              </span>
            </div>
            
            {/* Description */}
            {valueAddedProduct.description && (
              <div className="flex flex-col">
                <span className="text-gray-600 mb-1">Description:</span>
                <span className="font-medium text-gray-800 text-xs bg-gray-50 p-2 rounded border">
                  {valueAddedProduct.description}
                </span>
              </div>
            )}
            
            {/* Insured Value */}
            <div className="flex justify-between">
              <span className="text-gray-600">Insured Value:</span>
              <span className="font-medium text-green-600">
                {valueAddedProduct.value ? `R ${parseFloat(valueAddedProduct.value.toString()).toLocaleString()}` : 'Not set'}
              </span>
            </div>
            
            {/* Premium */}
            <div className="flex justify-between">
              <span className="text-gray-600">Premium:</span>
              <span className="font-medium text-purple-600">
                {valueAddedProduct.premium ? `R ${parseFloat(valueAddedProduct.premium.toString()).toLocaleString()}` : 'Not set'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Editing mode - show the form
  return (
    <div className="bg-white border-2 border-blue-200 rounded-lg shadow-md p-4 mb-4">
      <h4 className="font-bold text-lg mb-4 text-blue-700">Edit Value Added Product</h4>
      
      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="space-y-4 mb-6">
          {/* Row 1: Product Type, Value */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={`productType-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Item
              </label>
              <select
                id={`productType-${index}`}
                name="productType"
                value={formData.productType}
                onChange={handleChange}
                className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              >
                <option value="">Select product type</option>
                <option value="Vehicle">Vehicle</option>
                <option value="Building">Building</option>
                <option value="Home Content">Home Content</option>
                <option value="All Risk Item">All Risk Item</option>
                <option value="Trailer">Trailer</option>
                <option value="Caravan">Caravan</option>
                <option value="General">General</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor={`value-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
                Insured Value
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">R</span>
                </div>
                <input
                  type="text"
                  id={`value-${index}`}
                  name="value"
                  value={currencyDisplay.value}
                  onChange={handleChange}
                  onBlur={() => handleCurrencyBlur('value')}
                  className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Row 2: Description */}
          <div>
            <label htmlFor={`description-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
              Description
            </label>
            <textarea
              id={`description-${index}`}
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
              rows={4}
              placeholder="Enter product description"
            />
          </div>

          {/* Row 3: Premium */}
          <div>
            <label htmlFor={`premium-${index}`} className="block text-gray-700 font-bold mb-2 text-sm">
              Premium
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500">R</span>
              </div>
              <input
                type="text"
                id={`premium-${index}`}
                name="premium"
                value={currencyDisplay.premium}
                onChange={handleChange}
                onBlur={() => handleCurrencyBlur('premium')}
                className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

// Export both components
export default QuoteValueAddedModal;